import React, { useEffect } from 'react';
import { 
  EnvelopeIcon, 
  MapPinIcon,
  ChatBubbleLeftRightIcon 
} from '@heroicons/react/20/solid';
import Header from './shared/Header';
import Footer from './Footer';
import SEO from './SEO';

export default function ContactPage() {
  useEffect(() => {
    // Load HubSpot form script
    const script = document.createElement('script');
    script.src = "https://js.hsforms.net/forms/embed/22063250.js";
    script.defer = true;
    document.body.appendChild(script);
    
    return () => {
      // Cleanup on unmount
      try {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
        // Remove HubSpot form elements if necessary
        const hubspotFormDiv = document.querySelector('.hs-form-frame');
        if (hubspotFormDiv) {
          while (hubspotFormDiv.firstChild) {
            hubspotFormDiv.removeChild(hubspotFormDiv.firstChild);
          }
        }
      } catch (e) {
        console.error("Error removing HubSpot script:", e);
      }
    };
  }, []);

  return (
    <>
      <SEO 
        title="Contact Us"
        description="Get in touch with SharpeMetrix. Contact our team to learn more about our institutional-grade model portfolios and alternative data solutions for financial advisors."
        keywords="contact SharpeMetrix, financial advisor solutions, model portfolios inquiry, alternative data, quantitative strategies"
        url="/contact"
      />
      <Header />

      {/* Hero Section */}
      <div className="relative isolate overflow-hidden bg-bg-100 pt-14">
        <div aria-hidden="true" className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#2E5BFF] to-[#00D4FF] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-text-100 sm:text-6xl">
              Get in Touch
            </h1>
            <p className="mt-6 text-lg leading-8 text-text-200">
              Have questions about our methodology or model portfolios? Our team is ready to help you 
              explore how SharpeMetrix can benefit your practice.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <div className="bg-bg-200 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
            
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-text-100">
                Let's discuss your needs
              </h2>
              <p className="mt-6 text-lg leading-8 text-text-200">
                Whether you're interested in our model portfolios, Power List™ rankings, or have questions about our methodology, 
                we're here to help you understand how SharpeMetrix can enhance your investment process.
              </p>
              
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                    <EnvelopeIcon className="h-6 w-6 text-text-100" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-text-100">Email us</h3>
                    <p className="text-sm text-text-200">info@sharpemetrix.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-text-100" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-text-100">Schedule a call</h3>
                    <p className="text-sm text-text-200">Let's discuss your specific requirements</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                    <MapPinIcon className="h-6 w-6 text-text-100" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-text-100">Based in</h3>
                    <p className="text-sm text-text-200">North America</p>
                  </div>
                </div>
              </div>

              {/* What to Expect */}
              <div className="mt-12 rounded-lg bg-bg-100 p-6">
                <h3 className="text-lg font-semibold text-text-100 mb-4">What to expect:</h3>
                <ul className="space-y-3 text-text-200">
                  <li className="flex items-start gap-x-3">
                    <div className="flex-shrink-0 w-1.5 h-1.5 bg-primary-100 rounded-full mt-2"></div>
                    <span>Personalized consultation about your investment objectives</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <div className="flex-shrink-0 w-1.5 h-1.5 bg-primary-100 rounded-full mt-2"></div>
                    <span>Detailed explanation of our methodology and signals</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <div className="flex-shrink-0 w-1.5 h-1.5 bg-primary-100 rounded-full mt-2"></div>
                    <span>Demo of platform features and capabilities</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <div className="flex-shrink-0 w-1.5 h-1.5 bg-primary-100 rounded-full mt-2"></div>
                    <span>Discussion of implementation and integration options</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Contact Form */}
            <div>
              <div className="rounded-2xl bg-bg-100 p-8">
                <h3 className="text-xl font-semibold text-text-100 mb-6">Send us a message</h3>
                
                {/* HubSpot Form Container */}
                <div className="hs-form-frame" data-region="na1" data-form-id="6e20eb60-2b8c-46ef-b4de-c2be840e2f12" data-portal-id="22063250">
                  {/* Loading state while HubSpot form loads */}
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-100"></div>
                  </div>
                </div>
                
                {/* Fallback contact info if form doesn't load */}
                <div className="mt-8 pt-8 border-t border-bg-300">
                  <p className="text-sm text-text-200">
                    Form not loading? Email us directly at{' '}
                    <a href="mailto:info@sharpemetrix.com" className="text-primary-100 hover:text-primary-200">
                      info@sharpemetrix.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
} 